import { normalizeNameChoseongPositions } from './classMemberNameDisplay';
import { canWriteSchoolScopedDataCache } from './schoolDataCacheVersion';
import { isExternalProfileImageSource } from './memberProfileDisplay';
import { normalizeMemberStudentNumber } from './classMemberNumbers';

/** 브라우저 캐시 + 서버 저장: 클래스(학년)별 멤버 목록 */

export type ClassMemberGender = '' | '남' | '여';

/** 자리배치 그리드 좌표 (블럭 top-left, 0-based) */
export type ClassMemberSeatPosition = {
  row: number;
  col: number;
};

export const MEMBER_GROUP_NO_MIN = 1;
export const MEMBER_GROUP_NO_MAX = 10;

export interface ClassMemberRow {
  id: string;
  name: string;
  /** 화면 표시만 초성으로 가릴 글자 위치(1부터). 저장 이름과는 별개다. */
  nameChoseongPositions?: number[];
  gender: ClassMemberGender;
  /** 소그룹 번호(1~10). null이면 없음 */
  groupNo: number | null;
  /**
   * 고정 학생 번호(1~9999, 중간 빈 번호 허용). 없으면 목록 순서로 번호를 매긴다.
   * 표시는 항상 utils/classMemberNumbers 의 헬퍼로 계산한다.
   */
  studentNumber?: number | null;
  /** 입장번호(6자리 숫자, 000000 제외). 비어 있으면 서버가 무작위 번호를 채운다 */
  entryNumber: string;
  /** 그룹별 단일 자리배치. 없으면 미배치 */
  seatPosition?: ClassMemberSeatPosition | null;
  /** public/profile/{id}.svg (예: basic-0) 또는 관리자 프로필 카드의 공개 이미지 URL */
  profileImage?: string;
  /** 멤버 실사 프로필 Storage path */
  photoProfilePath?: string;
  /** 멤버 실사 프로필 public URL */
  photoProfileUrl?: string;
  /** 사진 프로필 캐시 버전 */
  photoProfileVersion?: number;
}

export const DEFAULT_MEMBER_PROFILE_IMAGE = 'basic-0';
export const MEMBER_ENTRY_NUMBER_LENGTH = 6;

/** 입장번호 6자리(000000 제외)인지 */
export function isValidEntryNumber(raw: unknown): boolean {
  const s = String(raw ?? '');
  return /^\d{6}$/.test(s) && s !== '000000';
}

/** 무작위 6자리 입장번호(000000 제외) — 새 멤버 기본값 */
export function generateMemberEntryNumber(): string {
  const buf = new Uint32Array(1);
  let s = '';
  do {
    crypto.getRandomValues(buf);
    s = String(buf[0] % 1_000_000).padStart(MEMBER_ENTRY_NUMBER_LENGTH, '0');
  } while (s === '000000');
  return s;
}

/** 멤버 소그룹 번호: 1~10만 허용. 없거나 범위 밖이면 null(없음) */
export function normalizeMemberGroupNo(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
  if (!Number.isFinite(n)) return null;
  const t = Math.trunc(n);
  if (t < MEMBER_GROUP_NO_MIN || t > MEMBER_GROUP_NO_MAX) return null;
  return t;
}

/** 입장번호: 숫자만 6자리면 그대로, 그 외(예전 4자리 포함)는 '' (미설정 — 서버가 기존 번호 유지 또는 무작위 발급) */
export function normalizeEntryNumber(raw: unknown): string {
  const digits = String(raw ?? '').replace(/\D/g, '');
  return isValidEntryNumber(digits) ? digits : '';
}

export function normalizeProfileImageId(raw: unknown): string {
  if (typeof raw !== 'string') return DEFAULT_MEMBER_PROFILE_IMAGE;
  const id = raw.trim();
  if (!id) return DEFAULT_MEMBER_PROFILE_IMAGE;
  if (/^(basic-\d+|b-\d+|c-\d+)$/.test(id)) return id;
  // 관리자 프로필 카드(webp, 공개 버킷 URL)는 정적 SVG 화이트리스트를 우회해 그대로 저장/복원한다
  if (isExternalProfileImageSource(id)) return id;
  // 사용자 프로필 카드(비공개 버킷 경로 참조)
  if (id.startsWith('mpath:') && id.length > 'mpath:'.length) return id;
  return DEFAULT_MEMBER_PROFILE_IMAGE;
}

export function memberProfileImageSrc(imageId?: string | null): string {
  return `/profile/${normalizeProfileImageId(imageId)}.svg`;
}

/** 클래스 멤버 설정 화면: 학년 아래 여러 그룹 */
export interface ClassMemberGroupMeta {
  id: string;
  name: string;
  sortOrder: number;
}

export interface ClassMemberGradeLayout {
  groups: ClassMemberGroupMeta[];
  membersByGroupId: Record<string, ClassMemberRow[]>;
}

export const CLASS_MEMBERS_CHANGED_EVENT = 'cp:class-members-changed';
const pendingLoads = new Set<string>();

function storageKey(workspaceId: string, gradeId: string): string {
  return `cp-class-members:${workspaceId}:${gradeId}`;
}
/** 학급 멤버 캐시 localStorage 키(다른 탭 변경 감지용) */
export const classMembersStorageKey = storageKey;

export function normalizeSeatPosition(raw: unknown): ClassMemberSeatPosition | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const row = typeof o.row === 'number' ? o.row : Number(o.row);
  const col = typeof o.col === 'number' ? o.col : Number(o.col);
  if (!Number.isFinite(row) || !Number.isFinite(col)) return null;
  const r = Math.trunc(row);
  const c = Math.trunc(col);
  if (r < 0 || c < 0) return null;
  return { row: r, col: c };
}

function normalizeRows(raw: unknown): ClassMemberRow[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row): ClassMemberRow | null => {
      if (!row || typeof row !== 'object') return null;
      const o = row as Record<string, unknown>;
      const id = typeof o.id === 'string' && o.id ? o.id : null;
      if (!id) return null;
      const name = typeof o.name === 'string' ? o.name : '';
      const nameChoseongPositions = normalizeNameChoseongPositions(o.nameChoseongPositions);
      const g = o.gender;
      const gender: ClassMemberGender = g === '남' || g === '여' ? g : '';
      const entryNumber = normalizeEntryNumber(o.entryNumber ?? o.entry_number);
      const groupNo = normalizeMemberGroupNo(o.groupNo ?? o.group_no);
      const studentNumber = normalizeMemberStudentNumber(o.studentNumber ?? o.student_number);
      const seatRaw = o.seatPosition ?? o.seat_position;
      const seatPosition = normalizeSeatPosition(seatRaw);
      const profileImage = normalizeProfileImageId(o.profileImage ?? o.profile_image);
      const photoProfilePathRaw = o.photoProfilePath ?? o.photo_profile_path;
      const photoProfileUrlRaw = o.photoProfileUrl ?? o.photo_profile_url;
      const photoProfileVersionRaw = o.photoProfileVersion ?? o.photo_profile_version;
      const photoProfilePath =
        typeof photoProfilePathRaw === 'string' && photoProfilePathRaw.trim()
          ? photoProfilePathRaw.trim()
          : undefined;
      const photoProfileUrl =
        typeof photoProfileUrlRaw === 'string' && photoProfileUrlRaw.trim()
          ? photoProfileUrlRaw.trim()
          : undefined;
      const photoProfileVersionNum = Number(photoProfileVersionRaw);
      const photoProfileVersion =
        Number.isFinite(photoProfileVersionNum) && photoProfileVersionNum > 0
          ? Math.trunc(photoProfileVersionNum)
          : undefined;
      const normalized: ClassMemberRow = { id, name, gender, groupNo, entryNumber, profileImage };
      if (nameChoseongPositions) normalized.nameChoseongPositions = nameChoseongPositions;
      if (studentNumber != null) normalized.studentNumber = studentNumber;
      if (seatPosition) normalized.seatPosition = seatPosition;
      if (photoProfilePath) normalized.photoProfilePath = photoProfilePath;
      if (photoProfileUrl) normalized.photoProfileUrl = photoProfileUrl;
      if (photoProfileVersion) normalized.photoProfileVersion = photoProfileVersion;
      return normalized;
    })
    .filter((v): v is ClassMemberRow => v != null);
}

function parseGroupMeta(raw: unknown): ClassMemberGroupMeta | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = typeof o.id === 'string' && o.id ? o.id : null;
  if (!id) return null;
  const name = typeof o.name === 'string' ? o.name : '';
  const sortOrder = typeof o.sortOrder === 'number' ? o.sortOrder : Number(o.sort_order) || 0;
  return { id, name, sortOrder };
}

function parseLayoutV2(raw: Record<string, unknown>): ClassMemberGradeLayout | null {
  const groupsRaw = raw.groups;
  if (!Array.isArray(groupsRaw)) return null;
  const groups = groupsRaw.map(parseGroupMeta).filter((g): g is ClassMemberGroupMeta => g != null);
  const membersObj = raw.membersByGroupId;
  const membersByGroupId: Record<string, ClassMemberRow[]> = {};
  if (membersObj && typeof membersObj === 'object') {
    for (const [gid, arr] of Object.entries(membersObj as Record<string, unknown>)) {
      membersByGroupId[gid] = normalizeRows(arr);
    }
  }
  return { groups, membersByGroupId };
}

export function flattenGradeLayout(layout: ClassMemberGradeLayout): ClassMemberRow[] {
  const sorted = [...layout.groups].sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id));
  const out: ClassMemberRow[] = [];
  for (const g of sorted) {
    out.push(...(layout.membersByGroupId[g.id] ?? []));
  }
  return out;
}

export function emptyGradeLayout(): ClassMemberGradeLayout {
  return { groups: [], membersByGroupId: {} };
}

function parseStoragePayload(rawText: string): { kind: 'v1'; members: ClassMemberRow[] } | { kind: 'v2'; layout: ClassMemberGradeLayout } | null {
  try {
    const parsed = JSON.parse(rawText) as unknown;
    if (Array.isArray(parsed)) {
      return { kind: 'v1', members: normalizeRows(parsed) };
    }
    if (parsed && typeof parsed === 'object') {
      const o = parsed as Record<string, unknown>;
      if (o.v === 1 && Array.isArray(o.members)) {
        return { kind: 'v1', members: normalizeRows(o.members) };
      }
      if (o.v === 2) {
        const layout = parseLayoutV2(o);
        if (layout) return { kind: 'v2', layout };
      }
    }
  } catch {
    return null;
  }
  return null;
}

function emitClassMembersChanged(workspaceId: string, gradeId: string): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(CLASS_MEMBERS_CHANGED_EVENT, { detail: { workspaceId, gradeId } }));
}

/** @deprecated flat-only; 서비스는 cacheClassMemberGradeLayout 사용 */
export function cacheClassMembers(workspaceId: string, gradeId: string, rows: ClassMemberRow[]): void {
  if (!workspaceId || !gradeId || typeof window === 'undefined' || !canWriteSchoolScopedDataCache(workspaceId)) return;
  try {
    const key = storageKey(workspaceId, gradeId);
    const next = JSON.stringify({ v: 1, members: rows });
    const prev = window.localStorage.getItem(key);
    if (prev === next) return;
    window.localStorage.setItem(key, next);
    emitClassMembersChanged(workspaceId, gradeId);
  } catch {
    // ignore quota
  }
}

export function cacheClassMemberGradeLayout(workspaceId: string, gradeId: string, layout: ClassMemberGradeLayout): void {
  if (!workspaceId || !gradeId || typeof window === 'undefined' || !canWriteSchoolScopedDataCache(workspaceId)) return;
  try {
    const payload = {
      v: 2 as const,
      groups: layout.groups.map((g) => ({ id: g.id, name: g.name, sortOrder: g.sortOrder })),
      membersByGroupId: layout.membersByGroupId,
    };
    const key = storageKey(workspaceId, gradeId);
    const next = JSON.stringify(payload);
    const prev = window.localStorage.getItem(key);
    if (prev === next) return;
    window.localStorage.setItem(key, next);
    emitClassMembersChanged(workspaceId, gradeId);
  } catch {
    // ignore quota
  }
}

export function loadClassMembers(workspaceId: string, gradeId: string): ClassMemberRow[] {
  if (!workspaceId || !gradeId) return [];
  const loadKey = `${workspaceId}|${gradeId}`;
  try {
    const raw = window.localStorage.getItem(storageKey(workspaceId, gradeId));
    if (!raw) {
      if (!pendingLoads.has(loadKey)) {
        pendingLoads.add(loadKey);
        void import('../services/classMembersService').then((mod) => {
          void mod.getClassMembers(workspaceId, gradeId).finally(() => {
            pendingLoads.delete(loadKey);
          });
        });
      }
      return [];
    }
    const parsed = parseStoragePayload(raw);
    if (!parsed) return [];
    if (parsed.kind === 'v1') return parsed.members;
    return flattenGradeLayout(parsed.layout);
  } catch {
    return [];
  }
}

/** `cp-class-members` 키가 있으면 true(파싱 실패는 별도 처리). */
export function hasClassMemberLocalStorageSnapshot(workspaceId: string, gradeId: string): boolean {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(storageKey(workspaceId, gradeId));
    return raw != null && raw.length > 0;
  } catch {
    return false;
  }
}

export function loadClassMemberGradeLayoutFromStorage(workspaceId: string, gradeId: string): ClassMemberGradeLayout | null {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey(workspaceId, gradeId));
    if (!raw) return null;
    const parsed = parseStoragePayload(raw);
    if (!parsed) return null;
    if (parsed.kind === 'v2') return parsed.layout;
    if (parsed.kind === 'v1') {
      if (parsed.members.length === 0) return { groups: [], membersByGroupId: {} };
      const syntheticId = 'local-v1';
      return {
        groups: [{ id: syntheticId, name: '기본', sortOrder: 0 }],
        membersByGroupId: { [syntheticId]: parsed.members },
      };
    }
    return null;
  } catch {
    return null;
  }
}
